/**
 * Applications report fixture — task 117.
 *
 * Org-wide rollup of every application that had activity in the selected
 * window, with distinct-user count, event count, total/billable/non-billable
 * hours, and average hours per user. Values are hand-authored for realism
 * against the Meridian Associates persona (see `employees.ts`) and match
 * the `ApplicationsReport` response shape in `types/reports.ts`.
 *
 * Invariants enforced by the Zod schema in `schemas/applicationsReport.ts`:
 *   - billableHours + nonBillableHours ≈ totalHours (rounded to 0.1h).
 *   - avgHoursPerUser ≈ totalHours / distinctUsers.
 *   - Rows are sorted totalHours DESC (server owns the default order).
 *   - All hour figures are 1-decimal rounded.
 */

import type { ApplicationsReportRow } from '@/types/reports';

export const APPLICATIONS_REPORT = [
  {
    app: 'chrome.exe',
    distinctUsers: 8,
    totalEvents: 4120,
    totalHours: 171.7,
    billableHours: 128.8,
    nonBillableHours: 42.9,
    avgHoursPerUser: 21.5,
  },
  {
    app: 'Outlook.exe',
    distinctUsers: 8,
    totalEvents: 2840,
    totalHours: 118.3,
    billableHours: 41.4,
    nonBillableHours: 76.9,
    avgHoursPerUser: 14.8,
  },
  {
    app: 'Code.exe',
    distinctUsers: 4,
    totalEvents: 2165,
    totalHours: 90.2,
    billableHours: 81.2,
    nonBillableHours: 9.0,
    avgHoursPerUser: 22.6,
  },
  {
    app: 'Teams.exe',
    distinctUsers: 8,
    totalEvents: 1710,
    totalHours: 71.3,
    billableHours: 14.3,
    nonBillableHours: 57.0,
    avgHoursPerUser: 8.9,
  },
  {
    app: 'Slack.exe',
    distinctUsers: 7,
    totalEvents: 1485,
    totalHours: 61.9,
    billableHours: 6.2,
    nonBillableHours: 55.7,
    avgHoursPerUser: 8.8,
  },
  {
    app: 'Excel.exe',
    distinctUsers: 6,
    totalEvents: 1220,
    totalHours: 50.8,
    billableHours: 38.1,
    nonBillableHours: 12.7,
    avgHoursPerUser: 8.5,
  },
  {
    app: 'Figma.exe',
    distinctUsers: 3,
    totalEvents: 915,
    totalHours: 38.1,
    billableHours: 30.5,
    nonBillableHours: 7.6,
    avgHoursPerUser: 12.7,
  },
  {
    app: 'zoom.exe',
    distinctUsers: 6,
    totalEvents: 760,
    totalHours: 31.7,
    billableHours: 19.0,
    nonBillableHours: 12.7,
    avgHoursPerUser: 5.3,
  },
  {
    app: 'notion.exe',
    distinctUsers: 4,
    totalEvents: 540,
    totalHours: 22.5,
    billableHours: 15.8,
    nonBillableHours: 6.7,
    avgHoursPerUser: 5.6,
  },
  {
    app: 'powerpnt.exe',
    distinctUsers: 3,
    totalEvents: 305,
    totalHours: 12.7,
    billableHours: 8.9,
    nonBillableHours: 3.8,
    avgHoursPerUser: 4.2,
  },
] as const satisfies readonly ApplicationsReportRow[];
